/**
 * Générateur — Attendu 660 — Résoudre des problèmes en s'appuyant sur la
 * propriété caractéristique de la médiatrice.
 *
 * Niveau : 6ème | Cluster : 31 | Domaine : Espace et géométrie
 *
 * 5 exercices / 5 formats :
 *   - pb_lieu_equidistant_qcm               : QCM 4 choix (8 contextes, 3
 *                                              variantes : lieu / transfert /
 *                                              caractérisation) — reconnaître
 *                                              « équidistant » ⟹ médiatrice.
 *   - pb_centre_cercle_2mediatrices_programme : programme_construction 7 tâches
 *                                              (2 cercles bleus + médiatrice,
 *                                              2 cercles rouges + médiatrice,
 *                                              puis O à leur intersection) pour
 *                                              trouver le centre d'un cercle
 *                                              passant par 3 points.
 *   - pb_figure_mixte_vf                    : V/F (3V+3F stricte) + figure SVG
 *                                              (cercle O + A,B,M,N codés).
 *   - pb_justification_trous                : texte à trous 6 modèles tous
 *                                              paramétrés par (A,B)[+C].
 *
 * Variété cible : ≥ 20 variantes uniques par format (contrainte auditGen).
 * Voir docs/fiches/FICHE_660.md et docs/fiches/PAYLOADS_660.md.
 */

import {
    randInt, randomChoice, randomChoiceMultiple, shuffle, pickLettresSains,
    makeQcm, makeVraiFaux, makeTexteTrou, makeProgrammeConstruction,
} from '../../../utils';

// ═══════════════════════════════════════════════════════════
// CONSTANTES
// ═══════════════════════════════════════════════════════════

const ATTENDU_ID = 660;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_LIEU   = 'Lieu équidistant (contexte)';
const L_CENTRE = "Centre inconnu d'un cercle (2 médiatrices)";
const L_VF_FIG = 'Figure mixte cercle + médiatrice (V/F)';
const L_JUSTIF = 'Justification PC (texte à trous)';

// Lettres valides — exclut I et O (ambiguïté avec 1, 0 + conventions « centre »/« milieu »)
const LETTRES = 'ABCDEFGHJKLMNPQRSTUVWYZ'.split('');

// ViewBox figure (Ex. 4)
const VIEWBOX = { w: 300, h: 220 };
const CENTER_FIG = { x: 150, y: 110 };
const RADIUS_FIG = 80;

// Constantes programme_construction (Ex. 2)
const PX_PER_CM = 30;
const VIEWBOX_PC = { w: 600, h: 400 };
const TOLERANCE_PC = 12;
const COLOR_BLEU = '#378ADD';
const COLOR_ROUGE = '#E24B4A';
const COLOR_VERT = '#1D9E75';
const COLOR_VIOLET = '#9333EA';

// ═══════════════════════════════════════════════════════════
// HELPERS COMMUNS
// ═══════════════════════════════════════════════════════════

/** Encode une chaîne SVG en base64, compatible navigateur + Node. */
const svgToBase64 = (svg) => {
    if (typeof btoa === 'function') {
        try { return btoa(unescape(encodeURIComponent(svg))); } catch (_e) { /* fallthrough */ }
    }
    return Buffer.from(svg, 'utf-8').toString('base64');
};

/** Tire n lettres distinctes dans un pool (par défaut LETTRES). */
const pickN = (n, pool = LETTRES) => pickLettresSains(pool, n);

/** Point sur un cercle (centre (cx, cy), rayon r, angle en radians). */
const pointOnCircle = (cx, cy, r, thetaRad) => ({
    x: cx + r * Math.cos(thetaRad),
    y: cy + r * Math.sin(thetaRad),
});

/**
 * Dessine `nbTicks` petits tirets d'égalité (||) au milieu du segment
 * [(x1,y1), (x2,y2)], perpendiculaires au segment, espacés de `gap` px.
 * nbTicks=1 = un seul trait ; nbTicks=2 = deux traits. Distingue visuellement
 * deux groupes d'égalités (ici : rayons vs équidistance).
 */
const drawEqualMark = (x1, y1, x2, y2, nbTicks = 2, tickLen = 6, gap = 3) => {
    const dx = x2 - x1, dy = y2 - y1;
    const len = Math.hypot(dx, dy);
    if (len < 1e-3) return '';
    const ux = dx / len, uy = dy / len;
    const nx = -uy, ny = ux;
    const mx = (x1 + x2) / 2, my = (y1 + y2) / 2;
    const offsets = nbTicks === 1 ? [0] : [-gap / 2, gap / 2];
    return offsets
        .map(off => {
            const cx = mx + off * ux, cy = my + off * uy;
            const x1t = cx - (tickLen / 2) * nx, y1t = cy - (tickLen / 2) * ny;
            const x2t = cx + (tickLen / 2) * nx, y2t = cy + (tickLen / 2) * ny;
            return `<line x1="${x1t.toFixed(1)}" y1="${y1t.toFixed(1)}" x2="${x2t.toFixed(1)}" y2="${y2t.toFixed(1)}" stroke="#000" stroke-width="1.3" stroke-linecap="round" />`;
        })
        .join('');
};

// ═══════════════════════════════════════════════════════════
// Ex. 1 — Lieu équidistant en contexte (QCM sans figure)
// ═══════════════════════════════════════════════════════════

const CONTEXTES_EX1 = [
    { entites: 'villages',         lieu: 'puits',              det: 'un'  },
    { entites: 'écoles',           lieu: 'arrêt de bus',       det: 'un'  },
    { entites: 'antennes relais',  lieu: 'point de mesure',    det: 'un'  },
    { entites: 'arbres',           lieu: 'nichoir',            det: 'un'  },
    { entites: 'tours de guet',    lieu: 'poste de garde',     det: 'un'  },
    { entites: 'phares',           lieu: 'balise',             det: 'une' },
    { entites: 'refuges',          lieu: 'centre de secours',  det: 'un'  },
    { entites: 'bornes',           lieu: 'drapeau',            det: 'un'  },
];

// Les énoncés visent le LIEU COMPLET (ensemble de TOUS les points équidistants) et
// non « un point » : sinon « au milieu du segment » est aussi une réponse correcte
// (le milieu est équidistant de A et B) → deux bonnes réponses. En demandant tout
// l'ensemble, « au milieu » devient une réponse INCOMPLÈTE donc fausse.

// ─── v3 (simulateur HTML validé) : trois variantes de PROBLÈME sur la ───
// propriété caractéristique, 3 leurres tirés dans un pool par variante :
//   - « lieu »            : ensemble de TOUS les points équidistants (le milieu
//                           « uniquement » est un leurre légitime : incomplet) ;
//   - « transfert »       : M sur la médiatrice, AM = d ⟹ BM = d (usage direct,
//                           figure à l'appui) — leurres moitié/double/« mesurer »/
//                           « BM change selon la position » ;
//   - « caractérisation » : MA = MB ⟹ M sur la médiatrice (réciproque) —
//                           leurres « LE milieu », équilatéral, AB = 2d, etc.
// Interdits (affirmations VRAIES, défaut C2 d'origine) : « isocèle en M »,
// « la droite (M-milieu) est perpendiculaire à (AB) ».

/** Distances « propres » (entiers et demi-entiers, en cm). */
const DIST_LIEU = ['3', '4', '5', '6', '7', '8', '4,5', '5,5', '7,5'];
const lieuNum = (s) => parseFloat(String(s).replace(',', '.'));
const lieuFr = (x) => String(x).replace('.', ',');

/** Figure « transfert » : segment [AB], médiatrice pointillée, M dessus, AM coté. */
const buildMediatriceDataUrl = (A, B, dFr) => {
    const svg = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 190" width="300" height="190">'
        + '<line x1="60" y1="150" x2="240" y2="150" stroke="currentColor" stroke-width="2"/>'
        + '<line x1="150" y1="20" x2="150" y2="178" stroke="currentColor" stroke-width="1.6" stroke-dasharray="6 4"/>'
        + '<rect x="143" y="143" width="7" height="7" fill="none" stroke="currentColor" stroke-width="1.2"/>'
        + '<line x1="60" y1="150" x2="150" y2="52" stroke="currentColor" stroke-width="1.4"/>'
        + '<line x1="240" y1="150" x2="150" y2="52" stroke="currentColor" stroke-width="1.4" stroke-dasharray="3 3"/>'
        + '<circle cx="60" cy="150" r="3" fill="currentColor"/><circle cx="240" cy="150" r="3" fill="currentColor"/>'
        + '<circle cx="150" cy="52" r="3.5" fill="currentColor"/>'
        + `<text x="46" y="168" font-size="15" font-weight="600" fill="currentColor">${A}</text>`
        + `<text x="246" y="168" font-size="15" font-weight="600" fill="currentColor">${B}</text>`
        + '<text x="159" y="50" font-size="15" font-weight="600" fill="currentColor">M</text>'
        + `<text x="66" y="96" font-size="13.5" font-weight="600" fill="currentColor">${A}M = ${dFr} cm</text>`
        + `<text x="196" y="96" font-size="14" font-weight="600" fill="currentColor">${B}M = ?</text>`
        + `<text x="156" y="32" font-size="12.5" fill="currentColor">médiatrice de [${A}${B}]</text>`
        + '</svg>';
    // Même idiome data-URI que le reste du fichier (base64) — harmonisation
    // 2026-07-28, le fichier ayant un historique « figure blanche » côté
    // rastériseur externe : un seul chemin d'encodage à surveiller.
    return `data:image/svg+xml;base64,${svgToBase64(svg)}`;
};

const genLieuEquidistantQcm = () => {
    // « M » réservé : les variantes transfert/caractérisation nomment le point
    // mobile M en dur — tirer A ou B = M donnerait « MM = 5 cm » et deux
    // points étiquetés M sur la figure (retouche 2026-07-28).
    const [A, B] = pickN(2, LETTRES.filter((l) => l !== 'M'));
    const ctx = randomChoice(CONTEXTES_EX1);
    const variante = randomChoice(['lieu', 'transfert', 'caracterisation']);
    let texte; let correct; let pool; let figure = null;

    if (variante === 'lieu') {
        // Le quantificateur n'est plus dans la question, il est dans le leurre :
        // « Au milieu du segment [AB] UNIQUEMENT » affirme une exclusivité, donc
        // reste faux sans qu'on ait à réclamer « tous les emplacements ». Ne pas
        // retirer « uniquement » du pool sans rétablir le quantificateur ici.
        texte = `<p>Deux ${ctx.entites} ${A} et ${B} sont marqués sur une carte.</p>`
            + `<p><b>Où peut-on placer ${ctx.det} ${ctx.lieu} à la même distance de ${A} et de ${B} ?</b></p>`;
        correct = `Sur la médiatrice du segment [${A}${B}].`;
        pool = [
            `Au milieu du segment [${A}${B}] uniquement.`,
            `Sur la droite (${A}${B}), entre ${A} et ${B}.`,
            `Sur le cercle de centre ${A} passant par ${B}.`,
            `Sur la perpendiculaire à (${A}${B}) passant par ${A}.`,
            `Sur le cercle de diamètre [${A}${B}].`,
        ];
    } else if (variante === 'transfert') {
        const dStr = randomChoice(DIST_LIEU);
        const d = lieuNum(dStr);
        texte = `<p>Le point <b>M est sur la médiatrice</b> du segment [${A}${B}], et ${A}M = ${dStr} cm.</p>`
            + `<p><b>Que vaut la distance ${B}M ?</b></p>`;
        correct = `${dStr} cm, car M est équidistant de ${A} et de ${B}.`;
        pool = [
            `${lieuFr(d / 2)} cm, la moitié de ${A}M.`,
            `${lieuFr(2 * d)} cm, le double de ${A}M.`,
            'On ne peut pas savoir sans mesurer sur la figure.',
            `${B}M change selon la position de M sur la médiatrice.`,
        ];
        figure = buildMediatriceDataUrl(A, B, dStr);
    } else {
        const dStr = randomChoice(DIST_LIEU);
        const d = lieuNum(dStr);
        texte = `<p>Un point M vérifie <b>M${A} = M${B} = ${dStr} cm</b>.</p>`
            + '<p><b>Quelle affirmation est-on SÛR de pouvoir faire ?</b></p>';
        correct = `M est sur la médiatrice du segment [${A}${B}].`;
        pool = [
            `M est le milieu du segment [${A}${B}].`,
            `Le segment [${A}${B}] mesure ${lieuFr(2 * d)} cm.`,
            `M est à ${dStr} cm du milieu de [${A}${B}].`,
            `Le triangle ${A}${B}M est équilatéral.`,
            `M est à égale distance de tous les points du segment [${A}${B}].`,
        ];
    }

    const choix = [correct, ...randomChoiceMultiple(pool, 3)];
    const payload = makeQcm(choix, correct, texte, opts(L_LIEU));
    if (figure) {
        payload.elements_additionnels = {
            version: 1,
            elements: [{ type: 'image', config: { imageUrl: figure, maxWidth: 'small' } }],
        };
    }
    payload._meta.generatorKey = 'pb_lieu_equidistant_qcm';
    payload._meta.params = { ...payload._meta.params, variante };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Ex. 2 — Centre inconnu d'un cercle (programme_construction, 4 tâches)
// ═══════════════════════════════════════════════════════════

const R_CM_CHOICES_EX2 = [4, 5, 6]; // rayons du cercle support
const D_PAIR_EX2 = [40, 50, 60];    // marges pour r_pair (>= AB/2 + d)

/**
 * Tire une configuration valide pour Ex. 2 :
 *   - cercle support de centre (Cx, Cy) et rayon r_px
 *   - 3 points A, B, C répartis sur le cercle (angles écartés de ≥ 40°)
 *   - rayons r_pair (pour médiatrice [AB]) et r_pair2 (pour médiatrice [BC])
 *   - tous les arcs doivent rester dans le viewBox 600×400
 */
const tirerConfigEx2 = () => {
    const maxTries = 200;
    const W = VIEWBOX_PC.w, H = VIEWBOX_PC.h;
    const bboxMargin = 12;

    for (let i = 0; i < maxTries; i++) {
        const rCm = randomChoice(R_CM_CHOICES_EX2);
        const rPx = rCm * PX_PER_CM;

        const d1 = randomChoice(D_PAIR_EX2);

        // Tire 3 angles θ_A, θ_B, θ_C ∈ [0, 2π) avec écart min 40° (≈ 0.698 rad)
        const minDeltaRad = 40 * Math.PI / 180;
        const thetas = [];
        let tries = 0;
        while (thetas.length < 3 && tries < 200) {
            const cand = Math.random() * 2 * Math.PI;
            const ok = thetas.every(t => {
                const dAbs = Math.abs(cand - t);
                const dMod = Math.min(dAbs, 2 * Math.PI - dAbs);
                return dMod >= minDeltaRad;
            });
            if (ok) thetas.push(cand);
            tries++;
        }
        if (thetas.length < 3) continue;

        // Placement du centre Cx, Cy (contrainte : cercle + arcs dans viewBox)
        const A = pointOnCircle(0, 0, rPx, thetas[0]);
        const B = pointOnCircle(0, 0, rPx, thetas[1]);
        const C = pointOnCircle(0, 0, rPx, thetas[2]);

        // Calcul rayons des arcs
        const AB_px = Math.hypot(A.x - B.x, A.y - B.y);
        const BC_px = Math.hypot(B.x - C.x, B.y - C.y);
        const rPair  = AB_px / 2 + d1;
        // Les deux familles d'arcs partagent le centre B : à moins de 24 px
        // d'écart (2× la tolérance de scoring), l'élève risque de valider un
        // arc pour l'autre. d2 est CHOISI pour garantir l'écart (le pool D_PAIR_EX2
        // seul ne produit que des écarts ≤ 20 → rejet aveugle quasi systématique).
        const d2Candidats = [30, 40, 50, 60, 70, 80]
            .filter(d => Math.abs((BC_px / 2 + d) - rPair) >= 24);
        if (d2Candidats.length === 0) continue;
        const d2 = randomChoice(d2Candidats);
        const rPair2 = BC_px / 2 + d2;

        // L'arc depuis A a rayon rPair. Calculons les bbox-extents
        const maxR = Math.max(rPair, rPair2);
        // Pour chaque point, la bbox de son arc : (p.x - maxR, p.y - maxR) à (p.x + maxR, p.y + maxR)
        // On veut tous les points tels que leur bbox reste dans [bboxMargin, W-bboxMargin]
        const minX = Math.min(A.x, B.x, C.x) - maxR;
        const maxX = Math.max(A.x, B.x, C.x) + maxR;
        const minY = Math.min(A.y, B.y, C.y) - maxR;
        const maxY = Math.max(A.y, B.y, C.y) + maxR;
        const width = maxX - minX;
        const height = maxY - minY;

        if (width > W - 2 * bboxMargin) continue;
        if (height > H - 2 * bboxMargin) continue;

        // Décaler la bbox pour être dans le viewBox avec un peu de marge (aléatoire).
        // shiftX, shiftY sont aussi les coords du centre O du cercle (initialement en (0,0)).
        const shiftX = bboxMargin - minX + Math.random() * (W - 2 * bboxMargin - width);
        const shiftY = bboxMargin - minY + Math.random() * (H - 2 * bboxMargin - height);

        const Ax = A.x + shiftX, Ay = A.y + shiftY;
        const Bx = B.x + shiftX, By = B.y + shiftY;
        const Cx2 = C.x + shiftX, Cy2 = C.y + shiftY;
        const Ox = shiftX, Oy = shiftY;

        // Vérification finale
        const arcs = [
            { px: Ax, py: Ay, r: rPair },
            { px: Bx, py: By, r: rPair },
            { px: Bx, py: By, r: rPair2 },
            { px: Cx2, py: Cy2, r: rPair2 },
        ];
        let ok = true;
        for (const a of arcs) {
            if (a.px - a.r < bboxMargin || a.px + a.r > W - bboxMargin) { ok = false; break; }
            if (a.py - a.r < bboxMargin || a.py + a.r > H - bboxMargin) { ok = false; break; }
        }
        if (!ok) continue;

        return {
            Ax, Ay, Bx, By, Cx: Cx2, Cy: Cy2,
            Ox, Oy,
            rCm,
            rPair, rPair2,
            rPairCm: Math.round(rPair / PX_PER_CM * 10) / 10,
            rPair2Cm: Math.round(rPair2 / PX_PER_CM * 10) / 10,
        };
    }

    // Fallback déterministe VALIDÉ dans le viewBox 600×400 :
    // A(180,170), B(400,170), C(430,280) → AB = 220, BC ≈ 114
    // rPair = 140 (arcs bleus ∈ [40;540]×[30;310]), rPair2 = 87 (arcs rouges
    // ∈ [313;517]×[83;367]), Δr = 53 ≥ 24 ; médiatrices : x=290 ∈ [83;257] et
    // (351,242)-(478,208) ; centre O = (290, 259) — tout dans le cadre.
    const A = { x: 180, y: 170 };
    const B = { x: 400, y: 170 };
    const Cp = { x: 430, y: 280 };
    const AB_px = Math.hypot(A.x - B.x, A.y - B.y);
    const BC_px = Math.hypot(B.x - Cp.x, B.y - Cp.y);
    const rPair  = AB_px / 2 + 30; // 140
    const rPair2 = BC_px / 2 + 30; // ≈ 87
    const Ox = 290;
    const Oy = 259.09; // circumcentre calculé de (A, B, C)
    return {
        Ax: A.x, Ay: A.y,
        Bx: B.x, By: B.y,
        Cx: Cp.x, Cy: Cp.y,
        Ox, Oy,
        rCm: Math.round(Math.hypot(Ox - A.x, Oy - A.y) / PX_PER_CM * 10) / 10,
        rPair, rPair2,
        rPairCm: Math.round(rPair / PX_PER_CM * 10) / 10,
        rPair2Cm: Math.round(rPair2 / PX_PER_CM * 10) / 10,
    };
};

/** Formate un nombre cm en label ("4" ou "4,5"). */
const cmLabel660 = (cm) => (Number.isInteger(cm) ? String(cm) : String(cm).replace('.', ','));

const genCentreCercle2MediatricesProgramme = () => {
    const [A_name, B_name, C_name] = pickN(3);
    const cfg = tirerConfigEx2();

    const points = [
        { name: A_name, x: cfg.Ax, y: cfg.Ay },
        { name: B_name, x: cfg.Bx, y: cfg.By },
        { name: C_name, x: cfg.Cx, y: cfg.Cy },
    ];

    const preShapes = [];

    const rPairCmStr  = cmLabel660(cfg.rPairCm);
    const rPair2CmStr = cmLabel660(cfg.rPair2Cm);

    // Calcul des 2 points d'intersection de chaque paire d'arcs (de même rayon).
    // Formule : pour 2 cercles de rayon r centrés en P1, P2, les 2 intersections
    // sont sur la médiatrice de [P1P2], à distance h = √(r² - (|P1P2|/2)²) du milieu.
    const interPair = (P1x, P1y, P2x, P2y, r) => {
        const d = Math.hypot(P2x - P1x, P2y - P1y);
        const Mx = (P1x + P2x) / 2;
        const My = (P1y + P2y) / 2;
        const h = Math.sqrt(Math.max(0, r * r - (d / 2) ** 2));
        const vx = -(P2y - P1y) / d;  // perpendiculaire unitaire
        const vy = (P2x - P1x) / d;
        return [{ x: Mx + h * vx, y: My + h * vy }, { x: Mx - h * vx, y: My - h * vy }];
    };
    const [med1_p1, med1_p2] = interPair(cfg.Ax, cfg.Ay, cfg.Bx, cfg.By, cfg.rPair);
    const [med2_p1, med2_p2] = interPair(cfg.Bx, cfg.By, cfg.Cx, cfg.Cy, cfg.rPair2);

    const tasks = [
        {
            id: 'arcA_1',
            consigne: `Trace en <b>bleu</b> le cercle de centre ${A_name} et de rayon ${rPairCmStr} cm.`,
            color: COLOR_BLEU,
            shape: { type: 'circle', cx: cfg.Ax, cy: cfg.Ay, r: cfg.rPair },
        },
        {
            id: 'arcB_1',
            consigne: `Trace en <b>bleu</b> le cercle de centre ${B_name} et de <b>même rayon</b> (${rPairCmStr} cm).`,
            color: COLOR_BLEU,
            shape: { type: 'circle', cx: cfg.Bx, cy: cfg.By, r: cfg.rPair },
        },
        {
            id: 'med1',
            kind: 'line',
            consigne: `Trace en <b>vert</b> la médiatrice de [${A_name}${B_name}] : droite passant par les <b>deux points d'intersection</b> des cercles bleus.`,
            color: COLOR_VERT,
            shape: { type: 'line', p1: med1_p1, p2: med1_p2 },
        },
        {
            id: 'arcB_2',
            consigne: `Trace en <b>rouge</b> le cercle de centre ${B_name} et de rayon ${rPair2CmStr} cm.`,
            color: COLOR_ROUGE,
            shape: { type: 'circle', cx: cfg.Bx, cy: cfg.By, r: cfg.rPair2 },
        },
        {
            id: 'arcC_2',
            consigne: `Trace en <b>rouge</b> le cercle de centre ${C_name} et de <b>même rayon</b> (${rPair2CmStr} cm).`,
            color: COLOR_ROUGE,
            shape: { type: 'circle', cx: cfg.Cx, cy: cfg.Cy, r: cfg.rPair2 },
        },
        {
            id: 'med2',
            kind: 'line',
            consigne: `Trace en <b>violet</b> la médiatrice de [${B_name}${C_name}] : droite passant par les <b>deux points d'intersection</b> des cercles rouges.`,
            color: COLOR_VIOLET,
            shape: { type: 'line', p1: med2_p1, p2: med2_p2 },
        },
        {
            id: 'centreO',
            consigne: `Place le point <b>O</b> (centre du cercle) à l'<b>intersection</b> des deux médiatrices.`,
            color: '#1E3A8A', // bleu foncé pour O
            shape: { type: 'circle', cx: cfg.Ox, cy: cfg.Oy, r: 0 },
            pointLabel: 'O',
        },
    ];

    // Texte OBLIGATOIRE (le serializer back refuse un énoncé vide au POST —
    // revue 2026-08-11) : l'OBJECTIF en une phrase — la justification
    // (OA = OB = OC donc O sur les deux médiatrices) reste tue, c'est ce que
    // la construction doit faire découvrir.
    const texte = `<p>Un cercle passe par les points ${A_name}, ${B_name} et ${C_name}, mais son centre a été perdu. Suis les étapes dans l'ordre pour retrouver le point O, centre du cercle.</p>`;

    const payload = makeProgrammeConstruction(
        {
            pxPerCm: PX_PER_CM,
            tolerance: TOLERANCE_PC,
            viewBox: VIEWBOX_PC,
            points,
            preShapes,
            tasks,
        },
        texte,
        opts(L_CENTRE),
    );
    payload._meta.generatorKey = 'pb_centre_cercle_2mediatrices_programme';
    return payload;
};

// ═══════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════
// Ex. 4 — Identification sur figure mixte (V/F sur figure)
// ═══════════════════════════════════════════════════════════

/**
 * Tire la configuration géométrique pour Ex. 4 :
 *   - cercle fixe (CENTER_FIG, RADIUS_FIG)
 *   - A, B sur le cercle, écart angulaire ≥ 40° et non-diamètre (|Δ| ∉ [175°, 185°])
 *   - M sur médiatrice de [AB], hors du cercle (distance ∈ [r+15, r+40])
 *   - N "ailleurs" : hors médiatrice, hors cercle, à distance ≥ 35 de A et B
 */
const tirerConfigEx4 = () => {
    const maxTries = 100;
    const Ox = CENTER_FIG.x, Oy = CENTER_FIG.y;
    const R = RADIUS_FIG;

    for (let i = 0; i < maxTries; i++) {
        const thetaA = Math.random() * 2 * Math.PI;
        const thetaB = Math.random() * 2 * Math.PI;
        const dAbs = Math.abs(thetaA - thetaB);
        const dMod = Math.min(dAbs, 2 * Math.PI - dAbs) * 180 / Math.PI;
        // Bande interdite large : la corde vaut 2R·sin(θ/2), fonction PLATE au
        // voisinage de 180°. Avec R = 80, un écart angulaire de 165° donne une
        // corde à 1,4 px du diamètre — indiscernable à l'œil, alors que la ligne
        // « [AB] est un diamètre » est comptée FAUSSE. Plafond à 140° : l'écart
        // au diamètre vaut alors 9,6 px, visible. Écarte du même coup le
        // quasi-milieu (O presque au milieu de [AB]).
        if (dMod < 40 || (dMod >= 140 && dMod <= 220)) continue;

        const A = pointOnCircle(Ox, Oy, R, thetaA);
        const B = pointOnCircle(Ox, Oy, R, thetaB);

        // Direction unitaire de [AB] et perpendiculaire (médiatrice)
        const dx = B.x - A.x, dy = B.y - A.y;
        const len = Math.hypot(dx, dy);
        if (len < 1e-3) continue;
        const ux = dx / len, uy = dy / len;
        const nx = -uy, ny = ux; // perpendiculaire à [AB]

        // M sur la médiatrice de [AB], à distance de O dans [R+15, R+40] (donc hors du cercle).
        // Comme A et B sont sur le cercle, OA = OB = R : O appartient à la médiatrice
        // (propriété caractéristique). La médiatrice passe donc par O et a la direction
        // perpendiculaire à [AB], soit (nx, ny). On paramétrise M = O + t * (nx, ny),
        // ainsi |M - O| = |t|. On choisit un signe aléatoire pour placer M d'un côté ou
        // de l'autre de la droite (AB), puis |t| ∈ [R+15, R+40].
        const sideM = randomChoice([-1, 1]);
        const tM = sideM * randInt(R + 15, R + 40);
        const Mx = Ox + tM * nx;
        const My = Oy + tM * ny;

        // M doit être dans le viewBox avec marge 15
        if (Mx < 15 || Mx > VIEWBOX.w - 15 || My < 15 || My > VIEWBOX.h - 15) continue;

        // N : tirage aléatoire "ailleurs" avec contraintes
        let N = null;
        for (let j = 0; j < 80; j++) {
            const nxCand = randInt(20, VIEWBOX.w - 20);
            const nyCand = randInt(20, VIEWBOX.h - 20);
            // Distance à la médiatrice : projection orthogonale sur (ux, uy)
            // (direction du segment, i.e. perpendiculaire à la médiatrice)
            // Distance à la médiatrice = |(Nx - Ox) * ux + (Ny - Oy) * uy|
            const distMed = Math.abs((nxCand - Ox) * ux + (nyCand - Oy) * uy);
            if (distMed < 25) continue;
            // Distance à A, B ≥ 35
            if (Math.hypot(nxCand - A.x, nyCand - A.y) < 35) continue;
            if (Math.hypot(nxCand - B.x, nyCand - B.y) < 35) continue;
            // Distance au cercle (|distance à O - R|) ≥ 15
            const distO = Math.hypot(nxCand - Ox, nyCand - Oy);
            if (Math.abs(distO - R) < 15) continue;
            // Distance à M ≥ 30 (séparation visuelle)
            if (Math.hypot(nxCand - Mx, nyCand - My) < 30) continue;
            N = { x: nxCand, y: nyCand };
            break;
        }
        if (!N) continue;

        return { Ax: A.x, Ay: A.y, Bx: B.x, By: B.y, Mx, My, Nx: N.x, Ny: N.y, Ox, Oy, R };
    }

    // Fallback déterministe
    return {
        Ax: 100, Ay: 80, Bx: 200, By: 80,
        Mx: 150, My: 30, Nx: 60, Ny: 180,
        Ox: 150, Oy: 110, R: 80,
    };
};

/** Construit le SVG figure Ex. 4 : cercle + A, B (sur cercle), M, N + codage. */
const buildFigureEx4Svg = (cfg, A_name, B_name, M_name, N_name) => {
    const { Ax, Ay, Bx, By, Mx, My, Nx, Ny, Ox, Oy, R } = cfg;
    const svgParts = [];
    svgParts.push(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${VIEWBOX.w} ${VIEWBOX.h}">`);
    // Cercle
    svgParts.push(`<circle cx="${Ox.toFixed(1)}" cy="${Oy.toFixed(1)}" r="${R.toFixed(1)}" fill="none" stroke="#333" stroke-width="1.5" />`);
    // Centre O (point + label)
    svgParts.push(`<circle cx="${Ox.toFixed(1)}" cy="${Oy.toFixed(1)}" r="2.5" fill="#333" />`);
    svgParts.push(`<text x="${(Ox + 5).toFixed(1)}" y="${(Oy + 14).toFixed(1)}" font-size="13" font-weight="bold">O</text>`);
    // Segments [OA], [OB] (rayons) — codés nbTicks=1
    svgParts.push(`<line x1="${Ox.toFixed(1)}" y1="${Oy.toFixed(1)}" x2="${Ax.toFixed(1)}" y2="${Ay.toFixed(1)}" stroke="#555" stroke-width="1" stroke-dasharray="3 2" />`);
    svgParts.push(`<line x1="${Ox.toFixed(1)}" y1="${Oy.toFixed(1)}" x2="${Bx.toFixed(1)}" y2="${By.toFixed(1)}" stroke="#555" stroke-width="1" stroke-dasharray="3 2" />`);
    svgParts.push(drawEqualMark(Ox, Oy, Ax, Ay, 1));
    svgParts.push(drawEqualMark(Ox, Oy, Bx, By, 1));
    // Segments [MA], [MB] (équidistance) — codés nbTicks=2
    svgParts.push(`<line x1="${Mx.toFixed(1)}" y1="${My.toFixed(1)}" x2="${Ax.toFixed(1)}" y2="${Ay.toFixed(1)}" stroke="#555" stroke-width="1" stroke-dasharray="3 2" />`);
    svgParts.push(`<line x1="${Mx.toFixed(1)}" y1="${My.toFixed(1)}" x2="${Bx.toFixed(1)}" y2="${By.toFixed(1)}" stroke="#555" stroke-width="1" stroke-dasharray="3 2" />`);
    svgParts.push(drawEqualMark(Mx, My, Ax, Ay, 2));
    svgParts.push(drawEqualMark(Mx, My, Bx, By, 2));
    // Points A, B, M, N
    const drawPoint = (x, y, name, labelOffsetX = 8, labelOffsetY = -6) => {
        svgParts.push(`<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="3" fill="#000" />`);
        const lx = Math.max(4, Math.min(VIEWBOX.w - 12, x + labelOffsetX));
        const ly = Math.max(12, Math.min(VIEWBOX.h - 4, y + labelOffsetY));
        svgParts.push(`<text x="${lx.toFixed(1)}" y="${ly.toFixed(1)}" font-size="13" font-weight="bold">${name}</text>`);
    };
    drawPoint(Ax, Ay, A_name);
    drawPoint(Bx, By, B_name);
    drawPoint(Mx, My, M_name);
    drawPoint(Nx, Ny, N_name);
    svgParts.push('</svg>');
    return svgParts.join('');
};

const genFigureMixteVf = () => {
    const cfg = tirerConfigEx4();
    const [A, B] = pickN(2);
    const reste = LETTRES.filter(l => l !== A && l !== B);
    const [M, N] = shuffle(reste).slice(0, 2);

    const svg = buildFigureEx4Svg(cfg, A, B, M, N);
    const imageUrl = `data:image/svg+xml;base64,${svgToBase64(svg)}`;

    // Pool VRAIES (6 candidats) et FAUSSES (5 candidats), tous textuellement distincts
    const poolV = [
        `O${A} = O${B}.`,
        `O appartient à la médiatrice du segment [${A}${B}].`,
        `${M}${A} = ${M}${B}.`,
        `${M} appartient à la médiatrice du segment [${A}${B}].`,
        `Le point ${N} n'est pas équidistant de ${A} et ${B}.`,
        `[${A}${B}] est une corde du cercle de centre O.`,
    ];
    const poolF = [
        `O est le milieu du segment [${A}${B}].`,
        `${N}${A} = ${N}${B}.`,
        `${M} appartient au cercle de centre O.`,
        `Tous les points équidistants de ${A} et ${B} sont sur le cercle de centre O.`,
        `Le segment [${A}${B}] est un diamètre du cercle.`,
    ];

    // Tirer 3V + 3F
    const chosenV = randomChoiceMultiple(poolV, 3);
    const chosenF = randomChoiceMultiple(poolF, 3);

    // SHUFFLE côté générateur (obligatoire avant makeVraiFaux)
    const merged = shuffle([
        ...chosenV.map(l => ({ ligne: l, reponse: 0 })),
        ...chosenF.map(l => ({ ligne: l, reponse: 1 })),
    ]);
    const lignes = merged.map(x => x.ligne);
    const bonnesReponses = merged.map(x => x.reponse);

    const texte =
        // Ne plus écrire que le cercle « contient » les 4 points : seuls A et B sont sur
        // le cercle (tirerConfigEx4 place M hors du cercle et N ailleurs). Sinon l'énoncé
        // contredit la clé « M/E appartient au cercle = FAUX ».
        // Ne dire que ce que la figure ne dit pas seule. L'ancienne consigne
        // énonçait « OA = OB » et « MA = MB » : deux lignes du pool VRAI données
        // mot pour mot, soit une ligne offerte dans 92,7 % des tirages.
        `<p>Le cercle a pour centre O. Sur la figure, deux longueurs égales portent le même nombre de tirets.</p>`;

    const payload = makeVraiFaux(lignes, bonnesReponses, texte, {
        ...opts(L_VF_FIG),
        elements_additionnels: {
            version: 1,
            elements: [{ type: 'image', config: { imageUrl } }],
        },
    });
    payload._meta.generatorKey = 'pb_figure_mixte_vf';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Ex. 5 — Justification d'un programme de résolution (texte à trous)
// ═══════════════════════════════════════════════════════════

// 6 modèles — tous paramétrés par (A, B) (+ C pour mA) pour garantir l'unicité.
const MODELES_EX5 = [
    {
        id: 'mA',
        arity: 3,
        build: (A, B, C) => ({
            phrase: `Pour trouver le centre O d'un cercle passant par trois points ${A}, ${B}, ${C}, je trace la {{médiatrice}} du segment [${A}${B}] puis celle du segment [${B}${C}]. Le centre est situé à leur {{intersection}}, car O${A} = O${B} = O${C}, donc O est équidistant de tous ces points.`,
            bonnes: ['médiatrice', 'intersection'],
        }),
    },
    {
        id: 'mB',
        arity: 2,
        build: (A, B) => ({
            phrase: `Dans un cercle de centre O, si [${A}${B}] est une corde, alors O${A} = O${B} puisqu'il s'agit des {{rayons}} du cercle. Donc O appartient à la {{médiatrice}} de [${A}${B}], et la perpendiculaire à [${A}${B}] passant par O coupe [${A}${B}] en son {{milieu}}.`,
            bonnes: ['rayons', 'médiatrice', 'milieu'],
        }),
    },
    {
        id: 'mC',
        arity: 2,
        build: (A, B) => ({
            phrase: `Pour placer un point à la même distance de deux lieux ${A} et ${B}, il faut que ce point soit {{équidistant}} de ${A} et de ${B}. D'après la propriété caractéristique, cet ensemble de points est exactement la {{médiatrice}} du segment [${A}${B}].`,
            bonnes: ['équidistant', 'médiatrice'],
        }),
    },
    {
        id: 'mD',
        arity: 2,
        build: (A, B) => ({
            phrase: `Quand je trace la médiatrice de [${A}${B}] au compas, je pointe le compas en ${A} et je trace un arc, puis je pointe en ${B} avec le {{même}} écartement. Les deux arcs se coupent en deux points équidistants de ${A} et ${B}, donc sur la {{médiatrice}}.`,
            bonnes: ['même', 'médiatrice'],
        }),
    },
    {
        id: 'mE',
        arity: 2,
        build: (A, B) => ({
            phrase: `Pour résoudre un problème où un point doit être à égale distance de ${A} et de ${B}, je reconnais l'énoncé de la propriété {{caractéristique}} : la médiatrice du segment [${A}${B}] est l'ensemble des points {{équidistants}} de ${A} et ${B}.`,
            bonnes: ['caractéristique', 'équidistants'],
        }),
    },
    {
        id: 'mF',
        arity: 2,
        build: (A, B) => ({
            phrase: `On cherche à tracer un cercle passant par ${A} et ${B} dont le centre appartient à une droite (d) donnée. Le centre cherché est donc à la fois sur (d) et à égale distance de ${A} et ${B} : c'est le point d'{{intersection}} de (d) avec la {{médiatrice}} du segment [${A}${B}].`,
            bonnes: ['intersection', 'médiatrice'],
        }),
    },
];

const DISTRACTEURS_EX5 = [
    'parallèle', 'bissectrice', 'diamètres', 'milieu', 'centre', 'cercle',
    'extrémités', 'différent', 'séparés', 'côté', 'corde', 'hauteur',
    'perpendiculaire', 'diagonales', 'intersections', 'sommet', 'axe',
];

const genJustificationTrous = () => {
    const modele = randomChoice(MODELES_EX5);
    let built;
    if (modele.arity === 3) {
        const [A, B, C] = pickN(3);
        built = modele.build(A, B, C);
    } else {
        const [A, B] = pickN(2);
        built = modele.build(A, B);
    }
    const { phrase, bonnes } = built;

    const poolDistincts = DISTRACTEURS_EX5.filter(d => !bonnes.includes(d));
    const nbDistracteurs = randInt(3, 4);
    const distracteurs = randomChoiceMultiple(poolDistincts, nbDistracteurs);

    // Texte OBLIGATOIRE (le serializer back refuse un énoncé vide au POST —
    // revue 2026-08-11) : une consigne d'une ligne, la phrase à trous reste
    // le coeur du format.
    const texte = '<p>Complète la phrase en glissant les étiquettes.</p>';

    const payload = makeTexteTrou(phrase, texte, {
        ...opts(L_JUSTIF),
        mode: 'drag',
        distracteurs,
        strict: false,
    });
    // sousType STRUCTUREL : pose par la branche qui a choisi le modele,
    // il route le coup de pouce cote eleve. Canal `contenu`, qui survit
    // au POST — jamais `_meta`, supprime a la sauvegarde.
    payload.contenu = { ...payload.contenu, sousType: modele.id };
    payload._meta.generatorKey = 'pb_justification_trous';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// REGISTRY & EXPORTS
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    pb_lieu_equidistant_qcm:                 genLieuEquidistantQcm,
    pb_centre_cercle_2mediatrices_programme: genCentreCercle2MediatricesProgramme,
    pb_figure_mixte_vf:                      genFigureMixteVf,
    pb_justification_trous:                  genJustificationTrous,
};

export const CATEGORIES = [
    { id: 'pb_lieu_equidistant', label: L_LIEU,   formatsList: [{ key: 'pb_lieu_equidistant_qcm',                 label: 'QCM' }] },
    { id: 'pb_centre_cercle',    label: L_CENTRE, formatsList: [{ key: 'pb_centre_cercle_2mediatrices_programme', label: 'Construction' }] },
    { id: 'pb_figure_mixte',     label: L_VF_FIG, formatsList: [{ key: 'pb_figure_mixte_vf',                      label: 'Vrai/Faux' }] },
    { id: 'pb_justification',    label: L_JUSTIF, formatsList: [{ key: 'pb_justification_trous',                  label: 'Texte à trous' }] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    genLieuEquidistantQcm,
    genCentreCercle2MediatricesProgramme,
    genFigureMixteVf,
    genJustificationTrous,
};
